/**
 * A content page's body (`pages.content`), read as a small set of formatting
 * a person can type in the admin's editor — and never as HTML
 * (docs/02-Frontend/Pages.md, ADR-058). One parser for both sides: the
 * storefront draws a page from it, and the admin's preview reads a draft back
 * with the same rules, so what an operator sees is what a shopper gets. Markup an admin wrote would be an injection
 * path into every visitor's page; these are parsed into plain data and drawn
 * as elements, so nothing in a body is ever interpreted by the browser.
 *
 * | Typed                        | Shown                   |
 * |------------------------------|-------------------------|
 * | `## Heading`, `### Smaller`  | a section heading       |
 * | `- item` (or `•`)            | a bulleted list         |
 * | `1. item` (or `١.`)          | a numbered list         |
 * | `**words**`                  | bold                    |
 * | `[words](/pages/terms)`      | a link: a path on the store, https, mailto or tel |
 * | a blank line                 | a new paragraph         |
 *
 * A body written as plain text — every page before this set existed — reads
 * exactly as it did: paragraphs, and a line break wherever there was one.
 */
const HEADING = /^(#{1,3})\s+(.+)$/;
const BULLET = /^[-•*]\s+(.+)$/;
const NUMBERED = /^[0-9٠-٩]{1,3}[.)]\s+(.+)$/;
const INLINE = /\*\*(.+?)\*\*|\[([^\]\n]+)\]\((\S+?)\)/g;
export function parseRichText(body) {
    const blocks = [];
    let paragraph = null;
    let list = null;
    let headings = 0;
    const close = () => {
        if (paragraph)
            blocks.push({ type: 'paragraph', lines: paragraph });
        if (list)
            blocks.push({ type: 'list', ...list });
        paragraph = null;
        list = null;
    };
    for (const raw of body.replace(/\r\n?/g, '\n').split('\n')) {
        const line = raw.trim();
        if (!line) {
            close();
            continue;
        }
        const heading = HEADING.exec(line);
        if (heading) {
            close();
            headings += 1;
            // The page's title is its only first-level heading, so `#` is a section too.
            const level = heading[1].length === 3 ? 3 : 2;
            blocks.push({ type: 'heading', level, id: `section-${headings}`, content: parseInline(heading[2]) });
            continue;
        }
        const bullet = BULLET.exec(line);
        const numbered = bullet ? null : NUMBERED.exec(line);
        const item = bullet ?? numbered;
        if (item) {
            const ordered = Boolean(numbered);
            if (paragraph || (list && list.ordered !== ordered))
                close();
            list ??= { ordered, items: [] };
            list.items.push(parseInline(item[1]));
            continue;
        }
        if (list)
            close();
        paragraph ??= [];
        paragraph.push(parseInline(line));
    }
    close();
    return blocks;
}
export function parseInline(text) {
    const parts = [];
    let last = 0;
    for (const match of text.matchAll(INLINE)) {
        const index = match.index ?? 0;
        if (index > last)
            parts.push({ type: 'text', text: text.slice(last, index) });
        if (match[1] !== undefined) {
            parts.push({ type: 'strong', text: match[1] });
        }
        else {
            const href = safeHref(match[3]);
            // A link to anywhere else keeps its words and loses the link.
            parts.push(href ? { type: 'link', text: match[2], href } : { type: 'text', text: match[2] });
        }
        last = index + match[0].length;
    }
    if (last < text.length)
        parts.push({ type: 'text', text: text.slice(last) });
    return parts;
}
/** A path on this store, an anchor on this page, or a web, mail or phone address — nothing a browser would run. */
export function safeHref(href) {
    if (/^\/(?!\/)/.test(href) || /^#[\w-]+$/.test(href))
        return href;
    if (/^(https?:\/\/|mailto:|tel:)/i.test(href))
        return href;
    return null;
}
/** The words alone — for a meta description, where formatting would read as noise. */
export function plainText(body) {
    return parseRichText(body)
        .flatMap((block) => block.type === 'heading' ? [block.content] : block.type === 'paragraph' ? block.lines : block.items)
        .map((inlines) => inlines.map((inline) => inline.text).join(''))
        .join(' ');
}
//# sourceMappingURL=rich-text.js.map